import { Paper, Title, Text, Table, Badge, Image, Avatar, ScrollArea } from '@mantine/core';
import { IconPhoto } from '@tabler/icons-react';
import { formatDate } from '@/lib/utils';
import type { Asset } from '@/types/models';

interface RecentAssetsProps {
  assets: Asset[];
  limit?: number;
}

const statusColors: Record<string, string> = {
  Available: 'green',
  Assigned: 'blue',
  Maintenance: 'yellow',
  Retired: 'gray',
  Lost: 'red',
};

export function RecentAssets({ assets, limit = 5 }: RecentAssetsProps) {
  // Sort by created_at and take the most recent
  const recentAssets = [...assets]
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, limit);

  if (recentAssets.length === 0) {
    return (
      <Paper withBorder p="lg" radius="md">
        <Title order={3} mb="md">
          Recent Assets
        </Title>
        <Text c="dimmed" ta="center" py="xl">
          No assets to display
        </Text>
      </Paper>
    );
  }

  return (
    <Paper withBorder p="lg" radius="md">
      <Title order={3} mb="md">
        Recent Assets
      </Title>
      <ScrollArea h={400}>
        <Table>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Photo</Table.Th>
              <Table.Th>Tag ID</Table.Th>
              <Table.Th>Description</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Added</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {recentAssets.map((asset) => (
              <Table.Tr key={asset.id}>
                <Table.Td>
                  {asset.photo_url ? (
                    <Image
                      src={asset.photo_url}
                      alt={asset.description}
                      w={40}
                      h={40}
                      radius="sm"
                      fit="cover"
                    />
                  ) : (
                    <Avatar color="gray" size={40} radius="sm">
                      <IconPhoto size={20} />
                    </Avatar>
                  )}
                </Table.Td>
                <Table.Td>
                  <Text fw={500}>{asset.tag_id}</Text>
                </Table.Td>
                <Table.Td>
                  <div>
                    <Text size="sm">{asset.description}</Text>
                    <Text size="xs" c="dimmed">
                      {asset.brand} {asset.model}
                    </Text>
                  </div>
                </Table.Td>
                <Table.Td>
                  <Badge color={statusColors[asset.status]} variant="light" size="sm">
                    {asset.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Text size="sm" c="dimmed">
                    {formatDate(asset.created_at)}
                  </Text>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </ScrollArea>
    </Paper>
  );
}
